import * as THREE from 'three';
import type { SceneAssets } from './assets';
import { tween, smoothstep } from './anim';

// The wooden box that frames the board, with a hinged lid that opens to reveal the playfield.
// Geometry reconstructed from the Unity Board.prefab (all primitive Cubes), adapted to the
// rebuild's board-at-origin layout (cell = 1): faithful FORM + ANIMATION, pragmatic placement.
//   - 4 perimeter walls (tall hinge wall at the far -Z edge, low exit wall at the near +Z edge).
//   - Lid = a pivot Group at the far top edge; the plate is a child offset toward +Z so rotating
//     the pivot about X (0 -> -135 deg) swings the lid up and back, opening it (Lid.anim, 0.833s).
const LID_OPEN_RAD = -(135 * Math.PI) / 180;
const LID_MS = 833;

export class BoxView {
  group = new THREE.Group();
  private lidPivot = new THREE.Group();
  private lidOpen = false;

  constructor(assets: SceneAssets) {
    // Original box wood = Board_Base_Color (rebuild `boardMap`), used by Unity BaseWood (lid) and
    // BoardSides (walls). Lid tint ~0.71, sides ~0.67.
    const woodMat = (light: boolean) =>
      new THREE.MeshStandardMaterial({
        map: assets.boardMap ?? null,
        color: assets.boardMap ? (light ? 0xd8c8a6 : 0xbcab88) : light ? 0x6b4f32 : 0x4a3520,
        roughness: 0.82,
        metalness: 0.04,
      });
    const sides = woodMat(false);

    // Perimeter walls: [width, height, depth, x, y, z] (board centered at origin, cell = 1).
    const walls: [number, number, number, number, number, number][] = [
      [0.35, 2.27, 11.18, -5.14, -0.42, -0.3], // left (runs along Z)
      [0.4, 2.27, 11.21, 5.18, -0.42, -0.28], // right
      [10.63, 1.33, 0.56, 0.05, 0.07, 5.26], // near/low wall (+Z, viewer side) — drawer exits over it
      [10.63, 2.3, 0.55, 0.05, -0.4, -5.58], // far/tall wall (-Z) — the lid hinge side
    ];
    for (const [w, h, d, x, y, z] of walls) {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), sides);
      m.position.set(x, y, z);
      m.castShadow = true;
      m.receiveShadow = true;
      this.group.add(m);
    }

    // Lid pivot sits on top of the far wall; the plate is offset toward +Z so it covers the board
    // when closed and swings up/back when open.
    this.lidPivot.position.set(0.05, 0.72, -5.55);
    const plate = new THREE.Mesh(new THREE.BoxGeometry(10.61, 0.5, 11.0), woodMat(true));
    plate.position.set(0, 0, 5.5);
    plate.castShadow = true;
    plate.receiveShadow = true;
    this.lidPivot.add(plate);
    // Translucent lip along the lid's free (+Z) edge (Unity FrontCoverLid, alpha ~0.79).
    const cover = new THREE.Mesh(
      new THREE.BoxGeometry(10.61, 0.78, 0.16),
      // Unity FrontCoverLid: translucent grey, alpha 0.785.
      new THREE.MeshStandardMaterial({ color: 0xbcbcbc, transparent: true, opacity: 0.785, roughness: 0.5 }),
    );
    cover.position.set(0, 0.1, 11.0);
    this.lidPivot.add(cover);
    this.group.add(this.lidPivot);
    this.lidPivot.rotation.x = 0; // start closed
  }

  openLid(): void {
    if (this.lidOpen) return;
    this.lidOpen = true;
    const from = this.lidPivot.rotation.x;
    tween(LID_MS, (t) => (this.lidPivot.rotation.x = from + (LID_OPEN_RAD - from) * t), {
      ease: smoothstep,
    });
  }

  closeLid(): void {
    if (!this.lidOpen) return;
    this.lidOpen = false;
    const from = this.lidPivot.rotation.x;
    tween(LID_MS, (t) => (this.lidPivot.rotation.x = from + (0 - from) * t), { ease: smoothstep });
  }
}
